import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { httpClient } from "@/shared/api/http-client";
import type {
  CreateLocaleInput,
  SupportedLocale,
  UpdateLocaleInput,
} from "../shared/compliance-types";

export const LOCALES_QUERY_KEY = ["compliance", "locales"] as const;

async function fetchLocales(): Promise<readonly SupportedLocale[]> {
  return httpClient<readonly SupportedLocale[]>("/api/admin/tos/locales");
}

export function useLocales() {
  return useQuery({
    queryKey: LOCALES_QUERY_KEY,
    queryFn: fetchLocales,
    // Locales drive the canonical-locale lookup used by document
    // creation; cache aggressively but invalidate on every CRUD op.
    staleTime: 5 * 60_000,
  });
}

async function createLocale(input: CreateLocaleInput): Promise<SupportedLocale> {
  return httpClient<SupportedLocale>("/api/admin/tos/locales", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
}

export function useCreateLocale() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createLocale,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LOCALES_QUERY_KEY });
    },
  });
}

interface UpdateLocaleParams {
  readonly code: string;
  readonly input: UpdateLocaleInput;
}

// Backend's `PATCH /locales/:code` is the single endpoint for both
// renaming and atomic canonical promotion — when `isCanonical: true`
// is set, the use case calls `swapCanonical()` which demotes the
// current canonical row in the same transaction. There is no separate
// `/promote-canonical` route, and PATCH `{ isCanonical: false }` is
// explicitly rejected (you promote another locale instead).
async function updateLocale({ code, input }: UpdateLocaleParams): Promise<SupportedLocale> {
  return httpClient<SupportedLocale>(`/api/admin/tos/locales/${encodeURIComponent(code)}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
}

export function useUpdateLocale() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateLocale,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LOCALES_QUERY_KEY });
    },
  });
}

async function deleteLocale(code: string): Promise<void> {
  await httpClient<void>(`/api/admin/tos/locales/${encodeURIComponent(code)}`, {
    method: "DELETE",
  });
}

export function useDeleteLocale() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteLocale,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LOCALES_QUERY_KEY });
    },
  });
}
